import { useId, type ReactNode } from 'react';
import {
  EBAY_RAW_CONDITION_IDS,
  PROFESSIONAL_GRADERS,
  PROFESSIONAL_GRADES,
  RAW_CARD_CONDITIONS,
  RAW_CARD_CONDITION_LABELS,
  type CardPresentation,
  type ProfessionalGrade,
  type ProfessionalGrader,
  type RawCardCondition,
} from '@tcvs/shared';

export function CardPresentationEditor({
  value,
  disabled,
  onChange,
  compact = false,
  actions,
}: {
  value?: CardPresentation;
  disabled?: boolean;
  compact?: boolean;
  actions?: ReactNode;
  onChange: (value: CardPresentation | undefined) => void;
}): JSX.Element {
  const conditionId = useId();
  const rawConditionControl =
    value?.kind === 'RAW' || (compact && value === undefined) ? (
      <select
        id={conditionId}
        value={value?.kind === 'RAW' ? value.condition : ''}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value === ''
              ? undefined
              : { kind: 'RAW', condition: event.target.value as RawCardCondition },
          )
        }
      >
        {compact ? <option value="">Select card condition…</option> : null}
        {RAW_CARD_CONDITIONS.map((condition) => (
          <option key={condition} value={condition}>
            {RAW_CARD_CONDITION_LABELS[condition]}
          </option>
        ))}
      </select>
    ) : null;
  return (
    <section
      className={`tcvs-card-presentation${compact ? ' tcvs-card-presentation-compact' : ''}`}
      aria-label="eBay card condition"
    >
      {!compact ? (
        <div className="tcvs-card-presentation-heading">
          <strong>eBay condition</strong>
          {value === undefined ? (
            <span className="tcvs-error">Required for Ready to List</span>
          ) : null}
        </div>
      ) : null}
      {compact ? (
        <div className="tcvs-card-presentation-condition-heading">
          {value?.kind === 'GRADED' ? (
            <span>Card condition</span>
          ) : (
            <label htmlFor={conditionId}>Card condition</label>
          )}
          <label className="tcvs-card-presentation-graded-toggle">
            <input
              type="checkbox"
              checked={value?.kind === 'GRADED'}
              disabled={disabled}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? { kind: 'GRADED', grader: 'PSA', grade: 10 }
                    : { kind: 'RAW', condition: 'NEAR_MINT_OR_BETTER' },
                )
              }
            />
            <span>Graded</span>
          </label>
        </div>
      ) : (
        <label>
          <span>Card type</span>
          <select
            value={value?.kind ?? ''}
            disabled={disabled}
            onChange={(event) => {
              if (event.target.value === 'RAW')
                onChange({ kind: 'RAW', condition: 'NEAR_MINT_OR_BETTER' });
              else if (event.target.value === 'GRADED')
                onChange({ kind: 'GRADED', grader: 'PSA', grade: 10 });
              else onChange(undefined);
            }}
          >
            <option value="">Unclassified</option>
            <option value="RAW">Raw</option>
            <option value="GRADED">Professionally graded</option>
          </select>
        </label>
      )}
      {compact ? (
        rawConditionControl
      ) : value?.kind === 'RAW' ? (
        <label htmlFor={conditionId}>
          <span>Card condition</span>
          {rawConditionControl}
          <small className="tcvs-muted">
            Seller Hub condition 4000 · descriptor {EBAY_RAW_CONDITION_IDS[value.condition]}
          </small>
        </label>
      ) : null}
      {actions !== undefined ? (
        <div className="tcvs-card-presentation-actions">{actions}</div>
      ) : null}
      {compact && value === undefined ? (
        <span className="tcvs-error tcvs-card-presentation-required">
          Required for Ready to List
        </span>
      ) : null}
      {value?.kind === 'GRADED' ? (
        <div className="tcvs-card-presentation-graded">
          <label>
            <span>Grader</span>
            <select
              value={value.grader}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...value, grader: event.target.value as ProfessionalGrader })
              }
            >
              {PROFESSIONAL_GRADERS.map((grader) => (
                <option key={grader} value={grader}>
                  {grader}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Grade</span>
            <select
              value={value.grade}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...value, grade: Number(event.target.value) as ProfessionalGrade })
              }
            >
              {[...PROFESSIONAL_GRADES].reverse().map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Certification number (optional)</span>
            <input
              value={value.certificationNumber ?? ''}
              maxLength={30}
              disabled={disabled}
              onChange={(event) => {
                const certificationNumber = event.target.value;
                const next: CardPresentation = { ...value };
                if (certificationNumber === '') delete next.certificationNumber;
                else next.certificationNumber = certificationNumber;
                onChange(next);
              }}
            />
          </label>
        </div>
      ) : null}
    </section>
  );
}
